import { UserRoles } from '@/_helpers/interfaces';
import type { LucideIcon } from 'lucide-vue-next';
import {
  Banknote,
  BarChart3,
  Bell,
  CreditCard,
  Database,
  Files,
  History,
  LayoutDashboard,
  RotateCcw,
  User,
  UserCog,
  Users,
} from 'lucide-vue-next';

export interface SidebarItem {
  title: string;
  url?: string;
  icon?: LucideIcon;
  isActive?: boolean;
  routerName?: string;
  allowedRoles: string[];
  items?: {
    title: string;
    url?: string;
    routerName: string;
    allowedRoles: string[];
    nameKey: string;
  }[];
}

export const SideBarNavMain: SidebarItem[] = [
  {
    title: 'sidebar.items.dashboard',
    routerName: 'dashboard',
    icon: LayoutDashboard,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.customers',
    icon: Users,
    isActive: false,
    allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
    items: [
      {
        title: 'sidebar.items.customer_listing',
        routerName: 'customers',
        allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
        nameKey: 'sidebar.items.customer_listing',
      },
      {
        title: 'sidebar.items.add_customer',
        routerName: 'create-customer',
        allowedRoles: [UserRoles.ADMIN],
        nameKey: 'sidebar.items.add_customer',
      },
      {
        title: 'sidebar.items.projects',
        routerName: 'projects',
        allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER],
        nameKey: 'sidebar.items.projects',
      },
    ],
  },
  {
    title: 'sidebar.items.orders',
    routerName: 'orders',
    icon: Files,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.APPROVER,
      UserRoles.ORDER_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.recurring_orders',
    routerName: 'recurring-orders',
    icon: RotateCcw,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.APPROVER,
      UserRoles.ORDER_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.payments',
    routerName: 'payments',
    icon: CreditCard,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PAYER,
      UserRoles.APPROVER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.sepa',
    routerName: 'sepa-payments',
    icon: Banknote,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PAYER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.statistics',
    routerName: 'statistics',
    icon: BarChart3,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
    ],
  },
  {
    title: 'sidebar.items.master_data',
    icon: Database,
    isActive: false,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
    items: [
      {
        title: 'sidebar.items.suppliers',
        routerName: 'list-suppliers',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PURCHASER,
          UserRoles.PAYER,
          UserRoles.ORDER_CLEARER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
          UserRoles.VIEWER,
        ],
        nameKey: 'sidebar.items.suppliers',
      },
      {
        title: 'sidebar.items.supplier_bank_accounts',
        routerName: 'list-supplier-bank-accounts',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PAYER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
        ],
        nameKey: 'sidebar.items.supplier_bank_accounts',
      },
      {
        title: 'sidebar.items.customer_bank_accounts',
        routerName: 'list-customer-bank-accounts',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PAYER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
        ],
        nameKey: 'sidebar.items.customer_bank_accounts',
      },
    ],
  },
];

export const SideBarNavSecondary: SidebarItem[] = [
  {
    title: 'sidebar.items.users',
    routerName: 'list-users',
    icon: UserCog,
    allowedRoles: [UserRoles.ADMIN],
  },
  {
    title: 'sidebar.items.notifications',
    routerName: 'notification-center',
    icon: Bell,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    title: 'sidebar.items.history_log',
    routerName: 'history-log',
    icon: History,
    allowedRoles: [UserRoles.ADMIN],
  },
  {
    title: 'common.nav.my_profile',
    routerName: 'my-profile',
    icon: User,
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
];

// Legacy export for backwards compatibility
export const SideBarLinks = [
  {
    nameKey: 'sidebar.items.dashboard',
    iconClass: 'icon-dashboard',
    routerName: 'dashboard',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.customers',
    iconClass: 'icon-customers',
    routerName: 'customers',
    allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
    subLinks: [
      {
        nameKey: 'sidebar.items.customer_listing',
        routerName: 'customers',
        allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER, UserRoles.VIEWER],
      },
      {
        nameKey: 'sidebar.items.add_customer',
        routerName: 'create-customer',
        allowedRoles: [UserRoles.ADMIN],
      },
      {
        nameKey: 'sidebar.items.projects',
        routerName: 'projects',
        allowedRoles: [UserRoles.ADMIN, UserRoles.APPROVER],
      },
      {
        nameKey: 'sidebar.items.assign_team',
        routerName: 'assign-team',
        allowedRoles: [UserRoles.ADMIN],
      },
      {
        nameKey: 'sidebar.items.edit_limits',
        routerName: 'edit-limits',
        allowedRoles: [UserRoles.ADMIN],
      },
      {
        nameKey: 'sidebar.items.edit_forms',
        routerName: 'edit-forms',
        allowedRoles: [UserRoles.ADMIN],
      },
    ],
  },
  {
    nameKey: 'sidebar.items.orders',
    iconClass: 'icon-documents-outline',
    routerName: 'orders',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.APPROVER,
      UserRoles.ORDER_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.recurring_orders',
    iconClass: 'icon-repeat',
    routerName: 'recurring-orders',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.APPROVER,
      UserRoles.ORDER_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.payments',
    iconClass: 'icon-purchase',
    routerName: 'payments',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PAYER,
      UserRoles.APPROVER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.statistics',
    iconClass: 'icon-statistics',
    routerName: 'statistics',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
    ],
  },
  {
    nameKey: 'sidebar.items.users',
    iconClass: 'icon-users',
    routerName: 'list-users',
    allowedRoles: [UserRoles.ADMIN],
  },
  {
    nameKey: 'common.nav.my_profile',
    iconClass: 'icon-profile',
    routerName: 'my-profile',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.notifications',
    iconClass: 'icon-notifications-center',
    routerName: 'notification-center',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
  {
    nameKey: 'sidebar.items.history_log',
    iconClass: 'icon-history-log',
    routerName: 'history-log',
    allowedRoles: [UserRoles.ADMIN],
  },
  {
    nameKey: 'sidebar.items.master_data',
    iconClass: 'icon-document',
    routerName: 'master-data',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PURCHASER,
      UserRoles.PAYER,
      UserRoles.ORDER_CLEARER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
    subLinks: [
      {
        nameKey: 'sidebar.items.suppliers',
        routerName: 'list-suppliers',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PURCHASER,
          UserRoles.PAYER,
          UserRoles.ORDER_CLEARER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
          UserRoles.VIEWER,
        ],
      },
      {
        nameKey: 'sidebar.items.supplier_bank_accounts',
        routerName: 'list-supplier-bank-accounts',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PAYER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
        ],
      },
      {
        nameKey: 'sidebar.items.customer_bank_accounts',
        routerName: 'list-customer-bank-accounts',
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.PAYER,
          UserRoles.PAYMENT_CLEARER,
          UserRoles.APPROVER,
        ],
      },
    ],
  },
  {
    nameKey: 'sidebar.items.sepa',
    iconClass: 'icon-purchase',
    routerName: 'sepa-payments',
    allowedRoles: [
      UserRoles.ADMIN,
      UserRoles.PAYER,
      UserRoles.PAYMENT_CLEARER,
      UserRoles.APPROVER,
      UserRoles.VIEWER,
    ],
  },
];
